import { useRouterState } from '@tanstack/react-router';
import { CheckCircle2Icon, GithubIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api';
import { useInstallationStatus, useRequestInstallUrl } from '@/lib/hooks/useGithub';
import { cn } from '@/lib/utils';

export function SettingsIntegrationsPage() {
  const { data, isLoading } = useInstallationStatus();
  const install = useRequestInstallUrl();
  const { location } = useRouterState();
  const search = new URLSearchParams(location.searchStr ?? '');
  const banner = search.get('status');

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <header className="mb-6">
        <h1 className="text-3xl font-semibold tracking-tight">Integrations</h1>
        <p className="mt-1 text-sm text-slate-500">
          Connect the GitHub App so Athena can watch every PR touching your spec, SDK, or docs.
        </p>
      </header>

      {banner && <InstallBanner status={banner} />}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <GithubIcon className="h-6 w-6" />
            <div>
              <CardTitle>GitHub App</CardTitle>
              <CardDescription>
                Permissions: Contents (read), Pull requests (read + write), Checks (read + write).
                No access to code outside the repos you pick.
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        {isLoading && <p className="text-sm text-slate-500">Checking install status…</p>}

        {data?.installed && data.installation ? (
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-sm text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
              <CheckCircle2Icon className="h-4 w-4" />
              Installed on <strong>{data.installation.github_account_login}</strong>
              {data.installation.github_account_type && (
                <span className="text-xs text-emerald-700 dark:text-emerald-400">
                  ({data.installation.github_account_type})
                </span>
              )}
            </div>

            <RepoList repos={data.installation.installed_repositories ?? []} />

            <div className="mt-6 flex gap-3">
              <Button variant="outline" onClick={() => install.mutate()} disabled={install.isPending}>
                {install.isPending ? 'Opening…' : 'Manage installation'}
              </Button>
            </div>
          </div>
        ) : data && !data.installed ? (
          <div>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Once you install, Athena will:
            </p>
            <ul className="mt-2 list-disc pl-5 text-sm text-slate-600 dark:text-slate-400">
              <li>Subscribe to pull_request events on the repos you pick.</li>
              <li>Re-run the relevant audit dimensions on each PR's head commit.</li>
              <li>Post a single comment per PR, edited in place as you push new commits.</li>
              <li>Silently skip PRs from bots (Dependabot, Renovate) and drafts.</li>
            </ul>
            <div className="mt-5">
              <Button onClick={() => install.mutate()} disabled={install.isPending}>
                <GithubIcon className="h-4 w-4" />
                {install.isPending ? 'Opening GitHub…' : 'Install GitHub App'}
              </Button>
              {install.isError && (
                <p className="mt-2 text-sm text-[var(--color-critical)]">
                  {install.error instanceof ApiError
                    ? JSON.stringify((install.error as ApiError).detail)
                    : (install.error as Error).message}
                </p>
              )}
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

function RepoList({ repos }: { repos: Array<{ id: number; full_name: string; private?: boolean }> }) {
  if (repos.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        No repositories selected. Click <em>Manage installation</em> to pick some.
      </p>
    );
  }
  return (
    <div>
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
        Repositories Athena can see ({repos.length})
      </p>
      <ul className="grid grid-cols-1 gap-1.5 text-sm md:grid-cols-2">
        {repos.map((r) => (
          <li key={r.id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 font-mono text-xs dark:border-slate-800">
            <span>{r.full_name}</span>
            {r.private && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                private
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function InstallBanner({ status }: { status: string }) {
  const messages: Record<string, { tone: 'success' | 'warning' | 'critical'; body: string }> = {
    installed: { tone: 'success', body: 'GitHub App installed. Athena will start watching PRs within a minute.' },
    cancelled: { tone: 'warning', body: 'Install cancelled. Nothing changed.' },
    invalid_state: { tone: 'critical', body: 'The install link expired. Start again from this page.' },
  };
  const fallback = { tone: 'critical' as const, body: `Unexpected status: ${status}` };
  const { tone, body } = messages[status] ?? (status.startsWith('github_error:')
    ? { tone: 'critical' as const, body: `GitHub returned an error during install: ${status.slice('github_error:'.length)}` }
    : fallback);
  const classes = {
    success: 'border-[var(--color-success)] bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
    warning: 'border-[var(--color-warning)] bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
    critical: 'border-[var(--color-critical)] bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-200',
  }[tone];
  return (
    <div role="status" aria-live="polite" className={cn('mb-6 rounded-md border p-3 text-sm', classes)}>
      {body}
    </div>
  );
}
